/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { P } from "@cd2/text"
import { useTheme } from "@cd2/theme"
import { DropdownMenu, Grid, IconButton, List, Placeholder } from "@cd2/components"
import { IComponentProp } from "@cd2/tools"
import { icons } from "../../constants/icons"
import { brandColors, themeColors } from "../EOSThemeProvider"

interface IProps {
  address?: {
    label?: string
    name?: string
    contact?: string
    line_1?: string
    line_2?: string
    line_3?: string
    line_4?: string
    city?: string
    county?: string
    country?: string
    postcode?: string
    email?: string
    phone?: string
    importer_id?: string
  }
  isSelected?: boolean
  menuItems?: IComponentProp<typeof DropdownMenu, "items">
  isLoading?: boolean
  highlightColor?: string
  hasError?: boolean

  onClick?(): void

  onEditClick?(): void

  onRemoveClick?(): void
}

export function AddressCard({
  address = {},
  onClick,
  isSelected,
  menuItems,
  onEditClick,
  onRemoveClick,
  isLoading,
  highlightColor,
  hasError,
}: IProps) {
  const { borderRadius, themeColor } = useTheme()

  const highlightedColor = highlightColor ?? themeColor("primary")

  const keys: (keyof typeof address)[] = [
    "name",
    "line_1",
    "line_2",
    "line_3",
    "line_4",
    "city",
    "county",
    "postcode",
    "country",
  ]
  // const coreAddress = cleanArray([name, line_1, line_2, line_3, city, county, postcode, country])

  // function cleanArray(arr: any[]) {
  //   return arr.map(el => el?.trim()).filter(Boolean)
  // }

  function AddressSection({ children, title }: { children: React.ReactNode; title: string }) {
    return (
      <Grid columns={[48, "auto"]} gap={16}>
        <b
          css={css`
            font-size: 10px;
            font-weight: bold;
            opacity: 0.4;
            text-transform: uppercase;
            display: block;
            color: ${isSelected ? "white" : "#333"};
            text-align: right;
            padding: 3px 0 0;
          `}
        >
          {title}
        </b>
        <P color={isSelected ? "white" : undefined} blockSpacing={1} size="small">
          {children}
        </P>
      </Grid>
    )
  }

  const LoadingIcon = () => (
    <div
      css={css`
        height: 30px;
        width: 30px;
        background-color: #eee;
        border-radius: 50%;
      `}
    />
  )

  const invalidAddressForExport = (address.postcode ?? "").length > 10

  return (
    <div
      css={css(
        css`
          padding: 14px 16px;
          position: relative;
          border: 1px solid ${hasError ? brandColors.blaze : "#ddd"};
          border-radius: ${borderRadius}px;
          background-color: ${isSelected
            ? invalidAddressForExport
              ? brandColors.blaze
              : highlightedColor
            : "white"};
          box-shadow: ${invalidAddressForExport
            ? `0 0 0 3px ${brandColors.blaze};`
            : isSelected
            ? `0 0 0 3px ${highlightedColor};`
            : "none"};
        `,
        onClick &&
          !isLoading &&
          css`
            cursor: pointer;
            transition: 0.3s;

            &:hover {
              background-color: ${isSelected
                ? invalidAddressForExport
                  ? brandColors.blaze
                  : highlightedColor
                : "#f5f5f5"};
            }
          `,
      )}
      onClick={onClick}
    >
      <Grid gap={8}>
        <AddressSection title="Address">
          {isLoading ? (
            ["120px", "170px", "100px", "100px", "40px"].map((width, i) => (
              <Placeholder key={i} width={width} />
            ))
          ) : (
            <React.Fragment>
              {!!address.importer_id && (
                <React.Fragment>
                  <b>Imported from Sage</b>
                  <br />
                </React.Fragment>
              )}
              {keys
                .filter(key => (address[key] ?? "").trim().length > 0)
                .map((key, i) => (
                  <React.Fragment key={key}>
                    {i !== 0 && <br />}
                    {address[key]}
                  </React.Fragment>
                ))}
            </React.Fragment>
          )}
        </AddressSection>
        {address.email && (
          <AddressSection title="Email">
            {isLoading ? <Placeholder width="200px" /> : address.email}
          </AddressSection>
        )}
        {address.phone && (
          <AddressSection title="Phone">
            {isLoading ? <Placeholder width="120px" /> : address.phone}
          </AddressSection>
        )}
        {address.label && (
          <AddressSection title="Label">
            {isLoading ? <Placeholder width="120px" /> : address.label}
          </AddressSection>
        )}
        {address.contact && (
          <AddressSection title="Contact">
            {isLoading ? <Placeholder width="120px" /> : address.contact}
          </AddressSection>
        )}
        {invalidAddressForExport && (
          <P color={isSelected ? "white" : undefined} weight={800}>
            This address has a Postcode over 10 characters which won{"'"}t be valid for export to
            Sage. Please amend the address.
          </P>
        )}
      </Grid>
      <div
        onClick={e => e.stopPropagation()}
        css={css`
          cursor: default;
          position: absolute;
          bottom: 8px;
          right: 8px;
        `}
      >
        <List justifyContent="flex-end" size={4} horizontal>
          {onRemoveClick && (
            <React.Fragment>
              {isLoading ? (
                <LoadingIcon />
              ) : (
                <IconButton
                  buttonStyle={isSelected ? "solid" : "text"}
                  icon={icons.cross}
                  iconSize={13}
                  onClick={onRemoveClick}
                />
              )}
            </React.Fragment>
          )}
          {onEditClick && (
            <React.Fragment>
              {isLoading ? (
                <LoadingIcon />
              ) : (
                <IconButton
                  buttonStyle={isSelected ? "solid" : "text"}
                  icon={icons.edit}
                  iconSize={13}
                  onClick={onEditClick}
                />
              )}
            </React.Fragment>
          )}
          {menuItems && menuItems.length > 0 && (
            <React.Fragment>
              {isLoading ? (
                <LoadingIcon />
              ) : (
                <DropdownMenu
                  target={() => (
                    <IconButton
                      buttonStyle={isSelected ? "solid" : "text"}
                      icon={icons.ellipsis}
                      iconSize={13}
                    />
                  )}
                  items={menuItems}
                />
              )}
            </React.Fragment>
          )}
        </List>
      </div>
    </div>
  )
}
